import { useApolloClient } from "@apollo/react-hooks"
import { useReducer } from "react"
import { SourceControlProvider } from "@modules/graphql/types"

import {
  ValidateSitePublicNameDocument,
  ValidateSitePublicNameQuery,
} from "@modules/site/shared/queries.generated"

type ValidateNameState = {
  loading: boolean
  validationError?: string
  error?: string
}

enum ValidateNameActionType {
  Loading,
  ValidationError,
  Error,
}

type ValidateNameActions =
  | { type: ValidateNameActionType.Loading; payload: boolean }
  | {
      type: ValidateNameActionType.ValidationError
      payload: string | undefined
    }
  | { type: ValidateNameActionType.Error; payload: string }

const initialState: ValidateNameState = {
  loading: false,
  validationError: undefined,
  error: undefined,
}

const validateNameReducer = (
  state: ValidateNameState,
  action: ValidateNameActions
): ValidateNameState => {
  switch (action.type) {
    case ValidateNameActionType.Error:
      return { ...state, error: action.payload, loading: false }
    case ValidateNameActionType.ValidationError:
      return { ...state, validationError: action.payload, loading: false }
    case ValidateNameActionType.Loading:
      return { ...state, loading: action.payload }
  }
}

export type UseValidateUniqueSiteNameArgs = {
  name: string
  organizationId?: string
  sourceOrganizationId?: string
  siteId?: string
  sourceControlProvider?: SourceControlProvider
}

export const useValidateUniqueSiteName = () => {
  const client = useApolloClient()
  const [{ loading, error, validationError }, dispatch] = useReducer(
    validateNameReducer,
    initialState
  )

  const clearError = () => {
    dispatch({
      type: ValidateNameActionType.ValidationError,
      payload: undefined,
    })
  }

  const verifySiteNameUniqueness = ({
    name,
    sourceOrganizationId,
    organizationId,
    siteId,
    sourceControlProvider = SourceControlProvider.Github,
  }: UseValidateUniqueSiteNameArgs) => {
    dispatch({ type: ValidateNameActionType.Loading, payload: true })

    return client
      .query<ValidateSitePublicNameQuery>({
        query: ValidateSitePublicNameDocument,
        variables: {
          name: name,
          sourceOrganizationId,
          sourceControlProvider,
          siteId,
          organizationId,
        },
        fetchPolicy: "network-only",
      })
      .then(result => {
        if (result?.data?.validateSitePublicName?.success) {
          dispatch({ type: ValidateNameActionType.Loading, payload: false })

          return true
        }

        dispatch({
          type: ValidateNameActionType.ValidationError,
          payload: `This site name already exists.`,
        })

        return false
      })
      .catch(() => {
        dispatch({
          type: ValidateNameActionType.Error,
          payload: `Something went wrong during the site name validation. Please, retry.`,
        })

        return false
      })
  }

  return [
    verifySiteNameUniqueness,
    { loading, error, validationError, clearError },
  ]
}
